import DeveloperModeIcon from '@mui/icons-material/DeveloperMode';
import InfoIcon from '@mui/icons-material/Info';
import ShieldIcon from '@mui/icons-material/Shield';
import {
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as NoteIcon } from '@work4all/assets/icons/note.svg';

import { Divider } from '@work4all/components/lib/dataDisplay/divider/Divider';
import { useHistoryStack } from '@work4all/components/lib/navigation/history-stack';

import { checkModuleRight, useModuleRights } from '@work4all/data';

import { ModuleAccessRightType } from '@work4all/models/lib/Enums/ModuleAccessRightType.enum';

import { useReleaseNotesDialogContext } from '../../../../contexts/ReleaseNotesDialogContext';
import { DebugInfoDialog } from '../../components/DebugInfoDialog/DebugInfoDialog';
import { DataProtection } from '../data-protection/DataProtection';
import { Design } from '../Design';
import { DevTools } from '../DevTools/DevTools';
import { MailboxExchangeSynchronization } from '../exchange-synchronization/components/MailboxExchangeSynchronization';
import { Firm } from '../Firm/Firm';
import { HomeScreen } from '../HomeScreen';
import { Security } from '../security/Security';

const PRIVACY_POLICY_URL = 'https://work4all.de/datenschutzerklaerung';

export const Settings = () => {
  const { t } = useTranslation();

  const [isDebugInfoDialogOpen, setDebugInfoDialogOpen] = useState(false);
  const { releaseNotesFile, version, openReleaseNotesDialog } =
    useReleaseNotesDialogContext();

  const { go } = useHistoryStack();

  const { rights } = useModuleRights();
  const canAccessExchangeConnector4 = checkModuleRight(
    rights,
    ModuleAccessRightType.FEATURE_EXCHANGE_CONNECTOR_4
  );

  return (
    <List>
      <Divider style={{ padding: '1rem 1rem 1rem 0' }}>
        {t('USER_MENU_SECTION.SETTINGS')}
      </Divider>

      <ListItem disablePadding>
        <ListItemButton
          onClick={() => {
            go({
              title: t('USER.DESIGN'),
              view: <Design />,
            });
          }}
        >
          <ListItemText primary={t('USER.DESIGN')} />
        </ListItemButton>
      </ListItem>
      <ListItem disablePadding>
        <ListItemButton
          onClick={() => {
            go({
              title: t('COMMON.DATAPROTECTION'),
              view: <DataProtection />,
            });
          }}
        >
          <ListItemText primary={t('COMMON.DATAPROTECTION')} />
        </ListItemButton>
      </ListItem>

      {canAccessExchangeConnector4 && (
        <ListItem disablePadding>
          <ListItemButton
            onClick={() => {
              go({
                title: t('USER.EXCHANGE_SYNCHRONIZATION'),
                view: <MailboxExchangeSynchronization />,
              });
            }}
          >
            <ListItemText primary={t('USER.EXCHANGE_SYNCHRONIZATION')} />
          </ListItemButton>
        </ListItem>
      )}
      <ListItem disablePadding>
        <ListItemButton
          onClick={() => {
            go({
              title: t('INPUTS.FIRM'),
              view: <Firm />,
            });
          }}
        >
          <ListItemText primary={t('INPUTS.FIRM')} />
        </ListItemButton>
      </ListItem>
      <ListItem disablePadding>
        <ListItemButton
          onClick={() => {
            go({
              title: t('USER.HOME_SCREEN'),
              view: <HomeScreen />,
            });
          }}
        >
          <ListItemText primary={t('USER.HOME_SCREEN')} />
        </ListItemButton>
      </ListItem>

      <ListItem disablePadding>
        <ListItemButton
          onClick={() => {
            go({
              title: t('USER.SECURITY'),
              view: <Security />,
            });
          }}
        >
          <ListItemText primary={t('USER.SECURITY')} />
        </ListItemButton>
      </ListItem>

      <Divider style={{ padding: '1rem 1rem 1rem 0' }}>
        {t('USER_MENU_SECTION.SUPPORT')}
      </Divider>

      <ListItem disablePadding>
        <ListItemButton
          disabled={!releaseNotesFile || !releaseNotesFile?.length}
          onClick={() => {
            openReleaseNotesDialog();
          }}
        >
          <ListItemIcon>
            <NoteIcon />
          </ListItemIcon>
          <ListItemText primary={t('RELEASE_NOTES.title', { version })} />
        </ListItemButton>
      </ListItem>

      <ListItem disablePadding>
        <ListItemButton
          onClick={() => {
            setDebugInfoDialogOpen(true);
          }}
        >
          <ListItemIcon>
            <InfoIcon />
          </ListItemIcon>
          <ListItemText primary={t('DEBUG_INFO.TITLE')} />
        </ListItemButton>
      </ListItem>

      <ListItem disablePadding>
        <ListItemButton
          onClick={() => go({ title: 'DevTools', view: <DevTools /> })}
        >
          <ListItemIcon>
            <DeveloperModeIcon />
          </ListItemIcon>
          <ListItemText primary={'DevTools'} />
        </ListItemButton>
      </ListItem>

      <ListItem disablePadding>
        <ListItemButton href={PRIVACY_POLICY_URL} target="_blank">
          <ListItemIcon>
            <ShieldIcon />
          </ListItemIcon>
          <ListItemText primary={t('SETTINGS.PRIVACY_POLICY')} />
        </ListItemButton>
      </ListItem>

      <DebugInfoDialog
        open={isDebugInfoDialogOpen}
        onClose={() => {
          setDebugInfoDialogOpen(false);
        }}
      />
    </List>
  );
};
